import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { additionalIncomeReportAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './AdditionalIncomeReportCreate.css';

const AdditionalIncomeReportCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    reportDate: new Date().toISOString().split('T')[0],
    incomeDescriptionAr: '',
    incomeDescriptionEng: '',
    amount: '',
    recipientNameAr: '',
    recipientNameEng: '',
    notes: '',
    isActive: 'true',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.reportDate.trim()) {
      newErrors.reportDate = t('additionalIncomeReport.create.errors.dateRequired');
    }
    if (!formData.incomeDescriptionAr.trim()) {
      newErrors.incomeDescriptionAr = t('additionalIncomeReport.create.errors.incomeDescriptionArRequired');
    }
    if (!formData.incomeDescriptionEng.trim()) {
      newErrors.incomeDescriptionEng = t('additionalIncomeReport.create.errors.incomeDescriptionEngRequired');
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t('additionalIncomeReport.create.errors.amountRequired');
    }
    if (!formData.recipientNameAr.trim()) {
      newErrors.recipientNameAr = t('additionalIncomeReport.create.errors.recipientNameArRequired');
    }
    if (!formData.recipientNameEng.trim()) {
      newErrors.recipientNameEng = t('additionalIncomeReport.create.errors.recipientNameEngRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        isActive: formData.isActive === 'true',
        notes: formData.notes || null,
      };
      await additionalIncomeReportAPI.create(submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/additional-income-report');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(t('additionalIncomeReport.create.messages.createError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('additionalIncomeReport.create.fields.date')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.reportDate ? 'is-invalid' : ''}`}
                  name="reportDate"
                  value={formData.reportDate}
                  onChange={handleChange}
                />
              </div>
              {errors.reportDate && (
                <div className="error-message">{errors.reportDate}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-dollar-sign"></i> {t('additionalIncomeReport.create.fields.amount')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-dollar-sign input-icon"></i>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`form-input ${errors.amount ? 'is-invalid' : ''}`}
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.create.placeholders.amount')}
                />
              </div>
              {errors.amount && (
                <div className="error-message">{errors.amount}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('additionalIncomeReport.create.fields.isActive')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <select
                  className="form-input"
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                >
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-sticky-note"></i> {t('additionalIncomeReport.create.fields.notes')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-sticky-note input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.create.placeholders.notes')}
                  maxLength={1000}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-globe"></i> {t('additionalIncomeReport.create.fields.incomeDescriptionEng')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-globe input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.incomeDescriptionEng ? 'is-invalid' : ''}`}
                  name="incomeDescriptionEng"
                  value={formData.incomeDescriptionEng}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.create.placeholders.incomeDescriptionEng')}
                  maxLength={255}
                />
              </div>
              {errors.incomeDescriptionEng && (
                <div className="error-message">{errors.incomeDescriptionEng}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-language"></i> {t('additionalIncomeReport.create.fields.incomeDescriptionAr')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-language input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.incomeDescriptionAr ? 'is-invalid' : ''}`}
                  name="incomeDescriptionAr"
                  value={formData.incomeDescriptionAr}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.create.placeholders.incomeDescriptionAr')}
                  maxLength={255}
                />
              </div>
              {errors.incomeDescriptionAr && (
                <div className="error-message">{errors.incomeDescriptionAr}</div>
              )}
          </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-globe"></i> {t('additionalIncomeReport.create.fields.recipientNameEng')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-globe input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.recipientNameEng ? 'is-invalid' : ''}`}
                  name="recipientNameEng"
                  value={formData.recipientNameEng}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.create.placeholders.recipientNameEng')}
                  maxLength={255}
                />
              </div>
              {errors.recipientNameEng && (
                <div className="error-message">{errors.recipientNameEng}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-language"></i> {t('additionalIncomeReport.create.fields.recipientNameAr')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-language input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.recipientNameAr ? 'is-invalid' : ''}`}
                  name="recipientNameAr"
                  value={formData.recipientNameAr}
                  onChange={handleChange}
                  placeholder={t('additionalIncomeReport.create.placeholders.recipientNameAr')}
                  maxLength={255}
                />
              </div>
              {errors.recipientNameAr && (
                <div className="error-message">{errors.recipientNameAr}</div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/additional-income-report')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('additionalIncomeReport.create.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('additionalIncomeReport.create.messages.createSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default AdditionalIncomeReportCreate;


